import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, Send, Trash2, Clock, CheckCircle, User } from 'lucide-react';
import { AdminLayout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { getAllQuestions, answerQuestion, deleteQuestion, getAdminMe } from '@/lib/api';

const AdminQuestions = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [answerDialog, setAnswerDialog] = useState({ open: false, question: null, answer: '' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, question: null });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      await getAdminMe();
      const response = await getAllQuestions();
      setQuestions(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = async () => {
    if (!answerDialog.answer.trim()) return;
    
    setSubmitting(true);
    try {
      await answerQuestion(answerDialog.question.id, answerDialog.answer);
      toast.success('Réponse envoyée');
      setAnswerDialog({ open: false, question: null, answer: '' });
      loadData();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.question) return;
    
    try {
      await deleteQuestion(deleteDialog.question.id);
      toast.success('Question supprimée');
      setDeleteDialog({ open: false, question: null });
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Questions des invités">
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-gray-500">Chargement...</div>
        </div>
      </AdminLayout>
    );
  }

  const unansweredCount = questions.filter(q => !q.answer).length;
  const answeredCount = questions.filter(q => q.answer).length;

  return (
    <AdminLayout title="Questions des invités">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
          <p className="text-2xl font-bold text-gray-900">{questions.length}</p>
          <p className="text-sm text-gray-500">Total</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
          <p className="text-2xl font-bold text-amber-600">{unansweredCount}</p>
          <p className="text-sm text-gray-500">En attente</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
          <p className="text-2xl font-bold text-green-600">{answeredCount}</p>
          <p className="text-sm text-gray-500">Répondues</p>
        </div>
      </div>

      {/* Questions List */}
      {questions.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Aucune question pour le moment</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {questions.map((question) => (
            <Card key={question.id} className={question.answer ? 'border-green-200' : 'border-amber-200'}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-terracotta/10 flex items-center justify-center">
                      <User className="w-5 h-5 text-terracotta" />
                    </div>
                    <div>
                      <CardTitle className="text-base">{question.rsvp?.family_name || 'Invité'}</CardTitle>
                      <p className="text-sm text-gray-500">{question.rsvp?.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {question.answer ? (
                      <Badge className="bg-green-100 text-green-800">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Répondu
                      </Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-800">
                        <Clock className="w-3 h-3 mr-1" />
                        En attente
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Question */}
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <p className="text-charcoal">{question.question}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    Posée le {new Date(question.created_at).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>

                {/* Answer if exists */}
                {question.answer && (
                  <div className="bg-green-50 border-l-4 border-green-500 rounded-r-lg p-4 mb-4">
                    <p className="text-charcoal">{question.answer}</p>
                    <p className="text-xs text-gray-500 mt-2">
                      Répondu le {new Date(question.answered_at).toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center gap-2">
                  {!question.answer && (
                    <Button
                      data-testid={`btn-answer-${question.id}`}
                      onClick={() => setAnswerDialog({ open: true, question, answer: '' })}
                      className="gap-2"
                    >
                      <Send className="w-4 h-4" />
                      Répondre
                    </Button>
                  )}
                  {question.answer && (
                    <Button
                      variant="outline"
                      onClick={() => setAnswerDialog({ open: true, question, answer: question.answer })}
                      className="gap-2"
                    >
                      <Send className="w-4 h-4" />
                      Modifier la réponse
                    </Button>
                  )}
                  <Button
                    data-testid={`btn-delete-${question.id}`}
                    variant="ghost"
                    onClick={() => setDeleteDialog({ open: true, question })}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Answer Dialog */}
      <Dialog open={answerDialog.open} onOpenChange={(open) => setAnswerDialog({ ...answerDialog, open })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Répondre à la question</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500 mb-1">Question de {answerDialog.question?.rsvp?.family_name} :</p>
              <p className="text-charcoal">{answerDialog.question?.question}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Votre réponse</label>
              <Textarea
                data-testid="answer-textarea"
                value={answerDialog.answer}
                onChange={(e) => setAnswerDialog({ ...answerDialog, answer: e.target.value })}
                placeholder="Écrivez votre réponse..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAnswerDialog({ open: false, question: null, answer: '' })}>
              Annuler
            </Button>
            <Button
              data-testid="btn-submit-answer"
              onClick={handleAnswer}
              disabled={submitting || !answerDialog.answer.trim()}
            >
              {submitting ? 'Envoi...' : 'Envoyer la réponse'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer cette question ?</DialogTitle>
          </DialogHeader>
          <p className="text-gray-600">
            Êtes-vous sûr de vouloir supprimer cette question ? Cette action est irréversible.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, question: null })}>
              Annuler
            </Button>
            <Button data-testid="btn-confirm-delete" variant="destructive" onClick={handleDelete}>
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminQuestions;
